import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Activity, ArrowRight } from 'lucide-react';
import { api, getMunicipioSlug } from '../services/api';
import FeedCard from './FeedCard';

/**
 * FeedWidget — Bloco compacto para a Home pública.
 *
 * - Renderiza somente se houver posts ativos.
 * - Chama GET /v2/feed?limit=4 com o municipio_slug atual.
 * - Mostra 4 FeedCards compactos + CTA "Ver tudo no feed →".
 */
export default function FeedWidget({ municipioSlug: propSlug, limit = 4 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [labels, setLabels] = useState({});
  const slug = propSlug || getMunicipioSlug();

  const load = useCallback(async () => {
    if (!slug) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [rf, rc] = await Promise.all([
        api.get('/v2/feed', { params: { municipio_slug: slug, limit } }),
        api.get('/v2/feed/categorias'),
      ]);
      // labels map
      const map = {};
      (rc.data?.categorias || []).forEach((c) => { map[c.key] = c.label; });
      setLabels(map);
      // só ativos não-expirados
      const now = Date.now();
      const live = (rf.data?.items || []).filter(
        (it) => it.status === 'ativo' && new Date(it.expira_em).getTime() > now,
      );
      setItems(live);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [slug, limit]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!items || items.length === 0) return null;

  return (
    <section className="my-8" data-testid="feed-widget-home">
      <div className="bg-gradient-to-br from-purple-50 via-white to-pink-50 rounded-2xl p-5 md:p-6 border border-purple-100">
        <header className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
            </span>
            <h2 className="text-lg md:text-xl font-bold">Acontecendo agora na cidade</h2>
          </div>
          <Link
            to={slug ? `/${slug}/feed` : '/feed'}
            data-testid="feed-widget-cta"
            className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-purple-700 hover:text-purple-900"
          >
            Ver tudo no feed <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {items.slice(0, limit).map((p) => (
            <FeedCard key={p.id} post={p} variant="compact" categoriasMap={labels} />
          ))}
        </div>

        <div className="mt-4 text-center sm:hidden">
          <Link
            to={slug ? `/${slug}/feed` : '/feed'}
            className="inline-flex items-center gap-1 text-sm font-medium text-purple-700"
          >
            Ver tudo no feed <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
